/**
 * Pathways v3: the one reader of the contract's error envelope (contract §1) and the one
 * map from a stable code to the sentence a user reads.
 *
 *   { "detail": { "code": "CAPACITY_REACHED", "message": "This pathway is full" } }
 *
 * The `code` is the key; the server's `message` is English developer prose and is never
 * shown. Every pathway surface (the practitioner's builder and dashboard, the learner's
 * pages) resolves its refusals through `pathwayErrorMessage`, so one code reads the same
 * everywhere.
 *
 * The sentences live in the "common" namespace (i18n/dictionaries/common/pathwayErrors.js)
 * because both the practitioner console and the public site show them.
 *
 * Pure: no React, no transport. The caller passes its bound `t` for "common".
 */
// Relative: pure modules, resolvable under Jest without a shim.
import {
  apiErrorCode,
  extractApiError,
  CAPABILITY_NOT_BUILT_CODE,
  CAPABILITY_NOT_IN_PLAN_CODE,
  PLAN_LIMIT_REACHED_CODE,
  TENANT_SUSPENDED_CODE,
} from "../lib/apiErrors";

/**
 * The machine code of a pathway refusal: `detail.code` when the body carries the §1
 * envelope, otherwise `null` (a network failure, a 5xx without a body, a Pydantic 422
 * whose `detail` is an array).
 *
 * @param {any} err  an axios error (or anything)
 * @returns {string|null}
 */
export function pathwayErrorCode(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail) && typeof detail.code === "string") {
    return detail.code;
  }
  return null;
}

/** Code -> "common" i18n key. Every code of the contract's §1 table has an entry. */
export const PATHWAY_ERROR_KEYS = Object.freeze({
  // auth and tenant
  SIGN_IN_REQUIRED: "pathwayErrors.SIGN_IN_REQUIRED",
  TENANT_CONTEXT_REQUIRED: "pathwayErrors.TENANT_CONTEXT_REQUIRED",
  // lookups
  PATHWAY_NOT_FOUND: "pathwayErrors.PATHWAY_NOT_FOUND",
  STEP_NOT_FOUND: "pathwayErrors.STEP_NOT_FOUND",
  FILE_NOT_FOUND: "pathwayErrors.FILE_NOT_FOUND",
  CERTIFICATE_NOT_FOUND: "pathwayErrors.CERTIFICATE_NOT_FOUND",
  // pathway writes
  PATHWAY_NOT_ACTIVATABLE: "pathwayErrors.PATHWAY_NOT_ACTIVATABLE",
  PATH_TYPE_LOCKED: "pathwayErrors.PATH_TYPE_LOCKED",
  PATHWAY_ARCHIVED: "pathwayErrors.PATHWAY_ARCHIVED",
  PROMPTS_COHORT_ONLY: "pathwayErrors.PROMPTS_COHORT_ONLY",
  MEDIA_NOT_FOUND: "pathwayErrors.MEDIA_NOT_FOUND",
  MEDIA_KIND_MISMATCH: "pathwayErrors.MEDIA_KIND_MISMATCH",
  // enrolment
  CAPACITY_REACHED: "pathwayErrors.CAPACITY_REACHED",
  PAYMENT_REQUIRED: "pathwayErrors.PAYMENT_REQUIRED",
  PAYMENT_NOT_REQUIRED: "pathwayErrors.PAYMENT_NOT_REQUIRED",
  CHECKOUT_IN_PROGRESS: "pathwayErrors.CHECKOUT_IN_PROGRESS",
  ALREADY_ENROLLED: "pathwayErrors.ALREADY_ENROLLED",
  // learner steps
  STEP_LOCKED: "pathwayErrors.STEP_LOCKED",
  STEP_NOT_QUIZ: "pathwayErrors.STEP_NOT_QUIZ",
  STEP_NOT_COMPLETABLE: "pathwayErrors.STEP_NOT_COMPLETABLE",
  QUIZ_ALREADY_PASSED: "pathwayErrors.QUIZ_ALREADY_PASSED",
  // nudge opt-out
  TOKEN_INVALID: "pathwayErrors.TOKEN_INVALID",
  // shape (422 `type`; UNKNOWN_ID may also come as the envelope)
  DUPLICATE_ID: "pathwayErrors.DUPLICATE_ID",
  UNKNOWN_ID: "pathwayErrors.UNKNOWN_ID",
  PATH_TYPE_ONE_LEVEL: "pathwayErrors.PATH_TYPE_ONE_LEVEL",
  STEP_TITLE_REQUIRED: "pathwayErrors.STEP_TITLE_REQUIRED",
  STEP_CONTENT_MISMATCH: "pathwayErrors.STEP_CONTENT_MISMATCH",
  VIDEO_URL_UNSUPPORTED: "pathwayErrors.VIDEO_URL_UNSUPPORTED",
  COHORT_DATES_INVALID: "pathwayErrors.COHORT_DATES_INVALID",
  TOO_MANY_OBJECTIVES: "pathwayErrors.TOO_MANY_OBJECTIVES",
  // phase 3 (contract §10.1, §10.3, §10.4, §10.5)
  STEP_NOT_PRACTICE: "pathwayErrors.STEP_NOT_PRACTICE",
  SUBMISSION_NOT_ENABLED: "pathwayErrors.SUBMISSION_NOT_ENABLED",
  SUBMISSION_EMPTY: "pathwayErrors.SUBMISSION_EMPTY",
  PHOTO_TOO_LARGE: "pathwayErrors.PHOTO_TOO_LARGE",
  PHOTO_TYPE_UNSUPPORTED: "pathwayErrors.PHOTO_TYPE_UNSUPPORTED",
  SUBMISSION_NOT_FOUND: "pathwayErrors.SUBMISSION_NOT_FOUND",
  SUBMISSION_ALREADY_REVIEWED: "pathwayErrors.SUBMISSION_ALREADY_REVIEWED",
  ATTENDANCE_NOT_COHORT_LIVE: "pathwayErrors.ATTENDANCE_NOT_COHORT_LIVE",
  LIVE_SERVICE_NOT_FOUND: "pathwayErrors.LIVE_SERVICE_NOT_FOUND",
  COURSE_NOT_FOUND: "pathwayErrors.COURSE_NOT_FOUND",
  COURSE_IN_PATHWAY: "pathwayErrors.COURSE_IN_PATHWAY",
  STEP_IDENTITY_CHANGED: "pathwayErrors.STEP_IDENTITY_CHANGED",
});

/**
 * Codes a Pydantic 422 carries as an item `type` (contract §1: a validator that needs a
 * stable code raises `PydanticCustomError(CODE, msg)`). They are the write-model rules the
 * builder also checks before a save (pathways/builderModel.js), so they reach the screen
 * only if the two ever disagree.
 *
 * The three phase-3 write rules (contract §10.1: LIVE_SERVICE_NOT_FOUND, COURSE_NOT_FOUND,
 * STEP_IDENTITY_CHANGED) are listed too. The contract names them "422" shape rules
 * without fixing the carrier; the §1 envelope is read first (`pathwayErrorCode`), so
 * whichever carrier the server uses, the same sentence is shown.
 */
const PYDANTIC_TYPE_CODES = Object.freeze([
  "DUPLICATE_ID", "UNKNOWN_ID", "PATH_TYPE_ONE_LEVEL", "STEP_TITLE_REQUIRED",
  "STEP_CONTENT_MISMATCH", "VIDEO_URL_UNSUPPORTED", "COHORT_DATES_INVALID", "TOO_MANY_OBJECTIVES",
  "LIVE_SERVICE_NOT_FOUND", "COURSE_NOT_FOUND", "STEP_IDENTITY_CHANGED",
]);

/**
 * Activation problem code (contract §4) -> "common" i18n key. A sentence naming a level
 * takes `{level}` ("Level 2 · Practice"), one naming a step takes `{step}`.
 */
export const ACTIVATION_PROBLEM_KEYS = Object.freeze({
  OBJECTIVE_REQUIRED: "pathwayErrors.problem.OBJECTIVE_REQUIRED",
  NO_STEPS: "pathwayErrors.problem.NO_STEPS",
  NO_MANDATORY_STEP: "pathwayErrors.problem.NO_MANDATORY_STEP",
  LEVEL_NEEDS_MANDATORY: "pathwayErrors.problem.LEVEL_NEEDS_MANDATORY",
  VIDEO_EMPTY: "pathwayErrors.problem.VIDEO_EMPTY",
  READING_EMPTY: "pathwayErrors.problem.READING_EMPTY",
  QUIZ_NO_QUESTIONS: "pathwayErrors.problem.QUIZ_NO_QUESTIONS",
  COHORT_DATES_REQUIRED: "pathwayErrors.problem.COHORT_DATES_REQUIRED",
  // phase 3 (contract §10.1), each naming its step
  PRACTICE_EMPTY: "pathwayErrors.problem.PRACTICE_EMPTY",
  LIVE_TIME_REQUIRED: "pathwayErrors.problem.LIVE_TIME_REQUIRED",
  LIVE_SERVICE_REQUIRED: "pathwayErrors.problem.LIVE_SERVICE_REQUIRED",
  LIVE_SERVICE_INACTIVE: "pathwayErrors.problem.LIVE_SERVICE_INACTIVE",
  COURSE_NOT_FOUND: "pathwayErrors.problem.COURSE_NOT_FOUND",
  COURSE_NOT_PUBLISHED: "pathwayErrors.problem.COURSE_NOT_PUBLISHED",
  COURSE_HAS_NO_LESSONS: "pathwayErrors.problem.COURSE_HAS_NO_LESSONS",
});

const UNKNOWN_PROBLEM_KEY = "pathwayErrors.problem.unknown";
const UNNAMED_LEVEL_KEY = "pathwayErrors.problem.unnamedLevel";
const UNNAMED_STEP_KEY = "pathwayErrors.problem.unnamedStep";
const INVALID_KEY = "pathwayErrors.invalid";

/** Codes lib/apiErrors already words, in every namespace that binds "common". */
const PLATFORM_CODES = new Set([
  CAPABILITY_NOT_IN_PLAN_CODE,
  CAPABILITY_NOT_BUILT_CODE,
  PLAN_LIMIT_REACHED_CODE,
  TENANT_SUSPENDED_CODE,
]);

const isProblem = (p) => Boolean(p) && typeof p === "object" && typeof p.code === "string";

/**
 * The problems of a `PATHWAY_NOT_ACTIVATABLE` refusal (a refused activate, or a refused
 * PUT of a published pathway), `[]` for any other error.
 *
 * @param {any} err
 * @returns {{ code: string, level_id?: string, step_id?: string }[]}
 */
export function activationProblems(err) {
  if (pathwayErrorCode(err) !== "PATHWAY_NOT_ACTIVATABLE") return [];
  const detail = err.response.data.detail;
  return Array.isArray(detail.problems) ? detail.problems.filter(isProblem) : [];
}

/**
 * One activation problem as a sentence. The caller resolves the level and step the
 * problem names to their display labels ("" when unknown: an unnamed placeholder is used).
 *
 * @param {{ code: string, level_id?: string, step_id?: string }} problem
 * @param {Function} tc  `useT("common")`
 * @param {{ level: string, step: string }} subjects  display labels of the named rows
 * @returns {string}
 */
export function activationProblemMessage(problem, tc, subjects = { level: "", step: "" }) {
  const key = ACTIVATION_PROBLEM_KEYS[problem.code] || UNKNOWN_PROBLEM_KEY;
  return tc(key, {
    level: subjects.level || tc(UNNAMED_LEVEL_KEY),
    step: subjects.step || tc(UNNAMED_STEP_KEY),
  });
}

/** The first known custom code of a Pydantic 422 array (see PYDANTIC_TYPE_CODES), or null. */
const pydanticTypeCode = (err) => {
  const detail = err?.response?.data?.detail;
  if (!Array.isArray(detail)) return null;
  return PYDANTIC_TYPE_CODES.find((code) => detail.some((d) => d && d.type === code)) || null;
};

/**
 * The sentence for any pathway refusal.
 *
 *   1. a §1 `code` this module knows -> its sentence;
 *   2. a Pydantic 422 carrying a known custom `type` -> that sentence; any other 422
 *      array -> "some values are not valid";
 *   3. a platform-wide code (plan gate, suspended practice) -> the sentence lib/apiErrors
 *      already owns for it, so a practitioner on a plan without `learn.courses` reads the
 *      same refusal here as on every other gated screen;
 *   4. anything else -> the caller's own fallback sentence (already localized). The
 *      server's English `message` is never shown.
 *
 * @param {any} err
 * @param {Function} tc        `useT("common")`
 * @param {string} fallback    localized sentence for an unrecognised failure
 * @returns {string}
 */
export function pathwayErrorMessage(err, tc, fallback) {
  const code = pathwayErrorCode(err);
  if (code && PATHWAY_ERROR_KEYS[code]) return tc(PATHWAY_ERROR_KEYS[code]);
  const typed = pydanticTypeCode(err);
  if (typed) return tc(PATHWAY_ERROR_KEYS[typed]);
  const detail = err?.response?.data?.detail;
  if (err?.response?.status === 422 && Array.isArray(detail)) return tc(INVALID_KEY);
  if (PLATFORM_CODES.has(apiErrorCode(err))) return extractApiError(err, fallback, tc);
  return fallback;
}
